import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ChildrenSelectorComponent, Student } from '../../shared/children-selector/children-selector.component';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import { TranslatePipe } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NotificationService } from '../../services/notification.service';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';

interface ObservationItem {
  idObs: number;
  video: any;
  files: any;
  title: string;
  description: string;
  date: string;
  isNew: boolean;
  isExpanded?: boolean;
}

@Component({
  selector: 'app-observation',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ChildrenSelectorComponent,
    PageTitleComponent,
    TranslatePipe,
  ],
  templateUrl: './observation.component.html',
  styleUrl: './observation.component.css',
})
export class ObservationComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  userData: any;
  DOMAINE_URL: string = environment.apiUrl;
  loading = false;
  totalObservationsCount: number | undefined;
  totalPagesFromApi: number | undefined;

  // Helper to build correct file URL
  resolveUrl(path: string): string {
    if (!path) {
      return '';
    }
    // Normalize slashes just in case
    path = path.replace(/\\/g, '/');
    
    // Look for 'ObsFile/' which is the common part we want to keep
    const keyword = 'ObsFile/';
    const index = path.indexOf(keyword);
    
    if (index !== -1) {
      // Extract everything starting from ObsFile/
      const relativePath = path.substring(index);
      return `https://plateforme.educanet.pro/${relativePath}`;
    }
    
    // Fallback: if it's already a full URL, return it
    if (path.startsWith('http')) {
        return path;
    }

    console.warn('Could not construct URL for path:', path);
    return '';
  }

  getFileUrl(file: any): string {
    if (!file || !file.path) {
      return '';
    }
    return this.resolveUrl(file.path);
  }

  constructor(
    private http: HttpClient, 
    private sanitizer: DomSanitizer,
    private notificationService: NotificationService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getSafeVideoUrl(videoPath: string): SafeResourceUrl {
    const url = this.resolveUrl(videoPath);
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  onStudentSelected(student: Student) {
    this.selectedStudent = student;
    this.loadObservations(student, this.currentPage);
  }

  onStudentsLoaded(students: Student[]) {
    this.students = students;
    // Auto-select first student if none selected
    if (!this.selectedStudent && students.length > 0) {
      this.selectedStudent = students[0];
    }
  }

  currentPage = 1;
  itemsPerPage = 10;

  students: Student[] = [];
  selectedStudent: Student | null = null;

  observations: ObservationItem[] = [];

  loadObservations(
    student: { idStudent: number; nameOrg?: string; batchId?: number },
    pageNumber: number
  ) {
    const storedData = localStorage.getItem('userData');
    if (!storedData) {
      console.error('No user data found in localStorage');
      return;
    }

    this.userData = JSON.parse(storedData);

    const nameOrg = student.nameOrg || this.userData.nameOrg;
    const idStudent = student.idStudent || this.userData.idStudent;

    const url = `${this.DOMAINE_URL}/observation/listObservationStudent`;

    const requestBody = {
      idStudent: idStudent,
      nameOrg: nameOrg,
      sizePage: this.itemsPerPage,
      numberPage: pageNumber,
    };

    this.loading = true; // afficher loader
    this.http.post<ApiResponse>(url, requestBody).subscribe({
      next: (data) => {
        this.observations = data.response.map((item: any) => {
          const mappedItem = {
            idObs: item.id,
            video: item.video || null,
            files: item.files || [],  
            title: item.observationNamme, 
            description: item.content || '',
            date: item.date,
            isNew: item.isNew || false,
            document: item.document,
            idStudent: item.idStudent,
            idTeacher: item.idTeacher,
            nameTeacher: item.nameTeacher,
            nameStudent: item.nameStudent,
            nameOrg: item.nameOrg,
            batchId: item.batchId,
          };
          return mappedItem;
        });
        this.loading = false;
        if (data.response && data.response.length > 0 && data.response[0].nbrObservations !== undefined) {
          this.totalObservationsCount = data.response[0].nbrObservations;
        } else {
          this.totalObservationsCount = this.observations.length;
        }
      },
      error: (err) => {
        console.error('Error fetching observations:', err);
        this.loading = false;
      },
    });
  }

  // Pagination getters
  get totalPages(): number {
    if (this.totalPagesFromApi !== undefined) {
      return this.totalPagesFromApi;
    }
    const pages = this.totalObservationsCount ? Math.ceil(this.totalObservationsCount / this.itemsPerPage) : 0;
    return pages;
  }

  get paginatedCahierList(): ObservationItem[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    const paginated = this.observations.slice(start, end);
    return paginated;
  }

  // Pagination navigation
  goToPage(page: number) {
    this.currentPage = page;
    if (this.selectedStudent) {
      this.loadObservations(this.selectedStudent, this.currentPage);
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      if (this.selectedStudent) {
        this.loadObservations(this.selectedStudent, this.currentPage);
      }
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      if (this.selectedStudent) {
        this.loadObservations(this.selectedStudent, this.currentPage);
      }
    }
  }

  getVisiblePages(): (number | string)[] {
    const totalPages = this.totalPages;
    const currentPage = this.currentPage;
    const visiblePages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) visiblePages.push(i);
    } else {
      visiblePages.push(1);
      if (currentPage <= 4) {
        for (let i = 2; i <= 5; i++) visiblePages.push(i);
        visiblePages.push('...');
        visiblePages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        visiblePages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) visiblePages.push(i);
      } else {
        visiblePages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++)
          visiblePages.push(i);
        visiblePages.push('...');
        visiblePages.push(totalPages);
      }
    }
    return visiblePages;
  }

  markAsSeen(observation: ObservationItem, studentId: number) {
    if (!observation || !studentId) return;

    this.notificationService.updateNotificationStatus({
      idType: observation.idObs,
      idStudent: studentId,
      nameType: 'Observation'
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Mettre à jour localement pour retirer le badge "Nouveau"
          observation.isNew = false;
          console.log(`Observation notification ${observation.idObs} marked as seen`);
          // Update notification counts after marking as seen
          this.updateNotificationCounts();
        },
        error: (err) => {
          console.error("Erreur lors de la mise à jour de l'observation :", err);
        },
      });
  }

  private updateNotificationCounts(): void {
    // Get userData from localStorage to access iduser
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    if (userData.iduser) {
      this.notificationService.getNotificationCounts(userData.iduser)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          error: (err) => console.error('Error updating notification counts:', err)
        });
    }
  }

  toggleExpand(item: ObservationItem) {
    this.observations.forEach((obs) => {
      if (obs !== item) {
        obs.isExpanded = false;
      }
    });

    item.isExpanded = !item.isExpanded;

    // Si l'observation est nouvelle et qu'on l'ouvre, on la marque comme vue
    if (item.isExpanded && item.isNew && this.selectedStudent) {
      this.markAsSeen(item, this.selectedStudent.idStudent);
    }
  }
}

interface ApiResponse {
  error: string | null;
  response: any[]; // Assuming the actual observations are in an array here
}
